import { Inject, Injectable } from '@angular/core';
import { SplunkRum, SessionBasedSampler } from '@splunk/otel-web';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { EnvironmentParams } from '@shared/models/environment-params';
import { WINDOW } from '@shared/tokens';
import { escapeUrlRegExp } from '@shared/utils/regex-utils';
import { Version as rxAppVersion } from '../../../environments/app-version';

export interface RumInitParams {
	companyId: number;
	contactId: number;
	applicationMode: string;
	orderId: number;
	rxId: string;
	apiEndpointFallback?: string | null;
	staticFilesEndpoint?: string | null;
}

@Injectable({
	providedIn: 'root'
})
export class RumObservabilityService {
	private readonly moduleName = 'RumObservabilityService';

	constructor(
		private readonly appSettingsService: AppSettingsService,
		private readonly logger: LoggerService,
		@Inject(WINDOW) private readonly window: Window
	) {}

	init(params: RumInitParams): void {
		const signalFx = this.appSettingsService.splunkSettings;

		if (!signalFx?.isEnabled || SplunkRum.inited) {
			return;
		}

		try {
			const ignoreUrls = this.getIgnoreUrls(this.appSettingsService.environmentParams, params);

			SplunkRum.init({
				realm: signalFx.realm,
				rumAccessToken: signalFx.rumAuth,
				applicationName: signalFx.app,
				deploymentEnvironment: signalFx.environment,
				version: rxAppVersion?.version,
				persistence: 'localStorage',
				disableAutomationFrameworks: true,
				disableBots: true,
				ignoreUrls,
				tracer: {
					sampler: new SessionBasedSampler({
						ratio: 0.7
					})
				},
				exporter: {
					otlp: true
				},
				// Find more here: https://github.com/signalfx/splunk-otel-js-web/tree/v3.0.0/packages/web#%EF%B8%8F-configuration-options
				instrumentations: {
					connectivity: true, // unexpected offline mode
					document: false,
					errors: true,
					fetch: true,
					frustrationSignals: false,
					interactions: false, // leave it to bi-capture
					longtask: true, // track long-running tasks & requests
					postload: false,
					xhr: true
				},
				globalAttributes: {
					microfrontend: 'rx-app',
					company: params.companyId,
					contact: params.contactId,
					applicationMode: params.applicationMode,
					orderId: params.orderId,
					rxId: params.rxId
				}
			});
		} catch (e) {
			this.logger.warn('SplunkRum.init failed', {
				module: this.moduleName,
				extendedParameters: { error: JSON.stringify(e) }
			});
		}
	}

	destroy(): void {
		try {
			if (SplunkRum.inited) {
				SplunkRum.deinit();
			}
		} catch (error) {
			this.logger.error('Failed to deinitialize SplunkRum:', {
				module: this.moduleName,
				extendedParameters: { error: JSON.stringify(error) }
			});
		}
	}

	private getIgnoreUrls(environmentParams: EnvironmentParams | null, rumInitParams: RumInitParams): RegExp[] {
		const rxAppUrls: string[] = [this.window.location.origin];

		if (rumInitParams.staticFilesEndpoint) {
			rxAppUrls.push(rumInitParams.staticFilesEndpoint);
		}
		if (rumInitParams.apiEndpointFallback) {
			rxAppUrls.push(rumInitParams.apiEndpointFallback);
		}
		if (environmentParams?.rxappBff?.urls) {
			const uniqueBffUrls = new Set(Object.values(environmentParams.rxappBff.urls).filter((url): url is string => !!url));

			rxAppUrls.push(...uniqueBffUrls);
		}

		const matchRxAppUrlsPattern = rxAppUrls.map(escapeUrlRegExp).join('|');
		const doNotMatchRxAppUrlsPattern = new RegExp(`^(?!${matchRxAppUrlsPattern})`);

		return [doNotMatchRxAppUrlsPattern];
	}
}
